import { projectRequest } from '../../../core/api/projectApiClient';
import type { ProjectRepository } from '../Domain/repository/ProjectRepository';
import type {
  CampaignGetResponse,
  CampaignSearchCommand,
  CampaignSearchResponse,
  EmptyResponse,
  Organization,
  OrganizationGetCommand,
  OrganizationSearchCommand,
  ProjectCreateCommand,
  ProjectCreateResponse,
  ProjectGetCommand,
  ProjectDonorsSumCommand,
  ProjectDonorsSumResponse,
  ProjectIdCheckCommand,
  ProjectIdCheckResponse,
  ProjectImageGetCommand,
  ProjectImageGetResponse,
  ProjectImageSearchCommand,
  ProjectImageSearchResponse,
  ProjectImageUploadCommand,
  ProjectImageUploadResponse,
  ProjectItem,
  ProjectPostCreateCommand,
  ProjectPostCreateResponse,
  ProjectPostDeleteCommand,
  ProjectPostDeleteResponse,
  ProjectPostGetCommand,
  ProjectPostGetResponse,
  ProjectPostSearchCommand,
  ProjectPostSearchResponse,
  ProjectPostUpdateCommand,
  ProjectPostUpdateResponse,
  ProjectsSearchCommand,
  ProjectsSearchResponse,
  ProjectUpdateCommand,
  ProjectUpdateResponse,
  ProjectsWithdrawSearchRequest,
  ProjectsWithdrawSearchResponse,
  SpProjectGetResponse,
  SpProjectGetWithUsersResponse,
  WithdrawCreateRequest,
  WithdrawGetResponse,
  WithdrawGetRequest,
  WithdrawProjectRequest,
  WithdrawPutRequest,
  WithdrawPutResponse,
  WithdrawSearchRequest,
  WithdrawSearchResponse,
} from '../Domain/entity/ProjectTypes';

export class ProjectRepositoryImpl implements ProjectRepository {
  /**
   * 프로젝트 목록 검색
   * - API: POST /projects/m/sch
   * - 용도: 조건(params) + limit/offset/sort 기반 프로젝트 리스트 조회
   */
  async searchProjects(command: ProjectsSearchCommand): Promise<ProjectsSearchResponse> {
    // NOTE: 실제 API Gateway는 trailing slash 요청(/projects/m/sch/)을 사용하는 케이스가 있어 그대로 맞춥니다.
    return projectRequest<ProjectsSearchResponse>('/projects/m/sch/', { body: command });
  }

  /**
   * 프로젝트 단건 조회
   * - API: POST /projects/get
   * - 용도: project_id 기반 프로젝트 상세 조회
   */
  async getProject(command: ProjectGetCommand): Promise<ProjectItem> {
    return projectRequest<ProjectItem>('/projects/get', { body: command });
  }

  /**
   * 프로젝트별 후원자 상태 건수 합계
   * - API: POST /projects/sum
   */
  async sumProjectDonors(command: ProjectDonorsSumCommand): Promise<ProjectDonorsSumResponse> {
    return projectRequest<ProjectDonorsSumResponse>('/projects/sum', { body: command });
  }

  /**
   * 프로젝트 ID 중복 확인
   * - API: POST /projects/checkid
   */
  async checkProjectId(command: ProjectIdCheckCommand): Promise<ProjectIdCheckResponse> {
    return projectRequest<ProjectIdCheckResponse>('/projects/checkid', { body: command });
  }

  /**
   * 프로젝트 생성
   * - API: POST /projects
   * - 용도: 프로젝트 생성
   * - 참고: 응답은 보통 insertedId 같은 생성 결과만 포함할 수 있어,
   *   최신 데이터 표시 목적이면 생성 후 get/search 재조회 필요할 수 있음
   */
  async createProject(command: ProjectCreateCommand): Promise<ProjectCreateResponse> {
    return projectRequest<ProjectCreateResponse>('/projects', { body: command });
  }

  /**
   * 프로젝트 저장(업데이트/업서트)
   * - API: POST /projects/put
   * - 용도: project_id 기준 프로젝트 정보 저장
   * - 참고: 응답이 modifiedCount 등 "처리 결과" 중심이면, 저장 후 get/search 재조회 필요할 수 있음
   */
  async updateProject(command: ProjectUpdateCommand): Promise<ProjectUpdateResponse> {
    return projectRequest<ProjectUpdateResponse>('/projects/put', { body: command });
  }

  /**
   * 이미지 업로드
   * - API: POST /mimages
   * - 용도: Base64 이미지를 업로드하고 URL을 반환받음
   */
  async uploadProjectImage(command: ProjectImageUploadCommand): Promise<ProjectImageUploadResponse> {
    return projectRequest<ProjectImageUploadResponse>('/mimages', { body: command });
  }

  /**
   * 이미지 상세 조회
   * - API: POST /mimages/get
   * - 용도: image_id 기반 이미지 상세 정보(URL 포함) 조회
   */
  async getProjectImage(command: ProjectImageGetCommand): Promise<ProjectImageGetResponse> {
    return projectRequest<ProjectImageGetResponse>('/mimages/get', { body: command });
  }

  /**
   * 이미지 목록 검색
   * - API: POST /mimages/m/sch
   */
  async searchProjectImages(command: ProjectImageSearchCommand): Promise<ProjectImageSearchResponse> {
    return projectRequest<ProjectImageSearchResponse>('/mimages/m/sch', { body: command });
  }

  /**
   * 프로젝트 상세 조회(회원,응원글,후원,키맨 정보 포함)
   * - API: POST /sp/projects
   */
  async getSpProject(command: ProjectGetCommand): Promise<SpProjectGetResponse> {
    return projectRequest<SpProjectGetResponse>('/sp/projects', { body: command });
  }

  /**
   * 프로젝트 상세 조회(회원정보 포함)
   * - API: POST /sp/projectsWusers
   */
  async getSpProjectWithUsers(command: ProjectGetCommand): Promise<SpProjectGetWithUsersResponse> {
    return projectRequest<SpProjectGetWithUsersResponse>('/sp/projectsWusers', { body: command });
  }

  /**
   * 기관(Organization) 단건 조회
   * - API: POST /organization/get
   * - 용도: organization_id 기반 기관 단건 조회
   */
  async getOrganization(command: OrganizationGetCommand): Promise<Organization> {
    return projectRequest<Organization>('/organization/get', { body: command });
  }

  /**
   * 기관(Organization) 검색
   * - API: POST /organization/m/sch
   * - 용도: params(필드/연산자/값) 기반 기관 목록 검색
   */
  async searchOrganizations(
    command: OrganizationSearchCommand,
  ): Promise<{ meta?: object; data?: Organization[] }> {
    return projectRequest<{ meta?: object; data?: Organization[] }>('/organization/m/sch', { body: command });
  }

  /**
   * 프로젝트 출금 가능 목록 조회(검색)
   * - API: POST /projects/withdraw/m/sch
   * - 용도: 출금 가능 프로젝트/출금 관련 목록 조건 검색
   * - 참고: request/response 스키마가 문서에 정의되어 있어 타입으로 고정
   */
  async searchWithdrawProjects(command: ProjectsWithdrawSearchRequest): Promise<ProjectsWithdrawSearchResponse> {
    return projectRequest<ProjectsWithdrawSearchResponse>('/projects/withdraw/m/sch', { body: command });
  }

  /**
   * 출금(withdraw) 생성/처리
   * - API: POST /withdraw
   * - 용도: withdraw 생성/처리
   * - 참고: api.json에 requestBody 스키마가 없어서 "JSON 객체"로만 타입 고정
   */
  async createWithdraw(body: WithdrawCreateRequest): Promise<EmptyResponse> {
    return projectRequest<EmptyResponse>('/withdraw', { body });
  }

  /**
   * 출금(withdraw) 단건 조회
   * - API: POST /withdraw/get
   * - 용도: withdraw 단건 조회
   */
  async getWithdraw(body: WithdrawGetRequest): Promise<WithdrawGetResponse> {
    return projectRequest<WithdrawGetResponse>('/withdraw/get', { body });
  }

  /**
   * 출금(withdraw) 검색
   * - API: POST /withdraw/m/sch
   * - 용도: withdraw 목록 조건 검색
   */
  async searchWithdraw(body: WithdrawSearchRequest): Promise<WithdrawSearchResponse> {
    return projectRequest<WithdrawSearchResponse>('/withdraw/m/sch', { body });
  }

  /**
   * 프로젝트에 대한 출금(withdraw) 생성/처리
   * - API: POST /withdraw/project
   * - 용도: 프로젝트 매핑 withdraw 생성/처리
   */
  async createWithdrawForProject(body: WithdrawProjectRequest): Promise<EmptyResponse> {
    return projectRequest<EmptyResponse>('/withdraw/project', { body });
  }

  /**
   * 출금(withdraw) 저장(업데이트)
   * - API: POST /withdraw/put
   * - 용도: withdraw 저장/업데이트
   */
  async updateWithdraw(body: WithdrawPutRequest): Promise<WithdrawPutResponse> {
    return projectRequest<WithdrawPutResponse>('/withdraw/put', { body });
  }

  /**
   * 캠페인 목록 검색
   * - API: POST /campaign/m/sch
   * - 용도: 캠페인 상태/조건(params) 기반 캠페인 리스트 조회
   */
  async searchCampaigns(command: CampaignSearchCommand): Promise<CampaignSearchResponse> {
    return projectRequest<CampaignSearchResponse>('/campaign/m/sch', { body: command });
  }

  /**
   * 캠페인 단건 조회
   * - API: POST /campaign/get
   * - 용도: campaign_id 기반 캠페인 단건 조회
   */
  async getCampaign(command: { campaign_id: string }): Promise<CampaignGetResponse> {
    return projectRequest<CampaignGetResponse>('/campaign/get', { body: command });
  }

  /**
   * 프로젝트 소식 등록
   * - API: POST /projectposts
   */
  async createProjectPost(command: ProjectPostCreateCommand): Promise<ProjectPostCreateResponse> {
    return projectRequest<ProjectPostCreateResponse>('/projectposts', { body: command });
  }

  /**
   * 프로젝트 소식 수정
   * - API: POST /projectposts/put
   */
  async updateProjectPost(command: ProjectPostUpdateCommand): Promise<ProjectPostUpdateResponse> {
    return projectRequest<ProjectPostUpdateResponse>('/projectposts/put', { body: command });
  }

  /**
   * 프로젝트 소식 삭제
   * - API: POST /projectposts/delete
   */
  async deleteProjectPost(command: ProjectPostDeleteCommand): Promise<ProjectPostDeleteResponse> {
    return projectRequest<ProjectPostDeleteResponse>('/projectposts/delete', { body: command });
  }

  /**
   * 프로젝트 소식 1건 조회
   * - API: POST /projectposts/get
   */
  async getProjectPost(command: ProjectPostGetCommand): Promise<ProjectPostGetResponse> {
    return projectRequest<ProjectPostGetResponse>('/projectposts/get', { body: command });
  }

  /**
   * 프로젝트 소식 검색
   * - API: POST /projectposts/m/sch
   */
  async searchProjectPosts(command: ProjectPostSearchCommand): Promise<ProjectPostSearchResponse> {
    return projectRequest<ProjectPostSearchResponse>('/projectposts/m/sch', { body: command });
  }
}


